'use client'

import { ClearIcon, SearchIcon } from '../../icons'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../constants/iconButtonAriaLabelMessageKeys'
import { DictionaryContext } from '../../../providers'
import React, { SubmitEvent, SyntheticEvent, useContext, useEffect, useRef, useState } from 'react'
import { useDebounce } from '../../../hooks/useDebounce'
import { Button, ButtonTypes, ButtonVariants } from '../Button'
import { SEARCH_ANALYTICS_ATTRIBUTE_PROPS, SEARCH_ANALYTICS_INPUT_NAME } from '../../../hooks/analytics/constants'
import { dispatchSearchInvoked } from '../../../hooks/analytics/searchInvokedAnalytics'

export interface SearchProps extends React.HTMLAttributes<HTMLFormElement> {
    placeholder?: string
    searchCallback: (query: string) => void
    delay?: number
    searchTerm?: string | undefined
    variant?: 'light' | 'dark'
    'data-testid-prefix'?: string
}

export const Search = ({
    placeholder = '',
    searchCallback,
    delay = 500,
    searchTerm,
    variant = 'light',
    'data-testid-prefix': testIdPrefix = '',
    ...props
}: SearchProps) => {
    const { onSubmit, className, ...restFormProps } = props
    const dictionary = useContext(DictionaryContext)
    const inputRef = useRef<HTMLInputElement>(null)
    const [query, setQuery] = useState<string>(searchTerm || '')
    const debouncedQuery = useDebounce(query, delay)

    useEffect(() => {
        setQuery(searchTerm || '')
    }, [searchTerm])

    useEffect(() => {
        dispatchSearchInvoked(debouncedQuery)
        searchCallback(debouncedQuery)
    }, [debouncedQuery])

    const inputHandler = (event: SyntheticEvent<HTMLInputElement>): void => {
        const target = event.target as HTMLInputElement
        setQuery(target.value)
    }

    const clearSearch = (): void => {
        setQuery('')
        if (inputRef?.current) inputRef.current.focus()
    }

    const defaultClasses = 'w-full text-sm text-text-tertiary inline-flex border focus-within:border-accent-600'
    let variantClasses = ''
    switch (variant) {
        case 'light':
            variantClasses = 'bg-shade-0 border-shade-200'
            break
        case 'dark':
            variantClasses = 'bg-shade-opacity-100 border-shade-100'
            break
    }
    const propClasses = className ?? ''
    const combinedClasses = [defaultClasses, variantClasses, propClasses].join(' ')

    const handleFormSubmit = (event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault()
        dispatchSearchInvoked(query)
        searchCallback(query)
        onSubmit?.(event)
    }

    return (
        <form
            className={combinedClasses}
            onSubmit={handleFormSubmit}
            {...SEARCH_ANALYTICS_ATTRIBUTE_PROPS}
            {...restFormProps}
        >
            <input
                ref={inputRef}
                name={SEARCH_ANALYTICS_INPUT_NAME}
                type="text"
                className="search h-12 px-3 inline-flex items-center bg-transparent min-w-0 outline-0 grow"
                placeholder={placeholder}
                onChange={inputHandler}
                value={query}
                data-testid={`${testIdPrefix}search-input`}
            />
            {query !== '' && (
                <Button
                    className="shrink-0"
                    variant={ButtonVariants.QUIET}
                    icon={<ClearIcon />}
                    onClick={() => clearSearch()}
                    data-testid={`${testIdPrefix}search-clear-button`}
                    aria-label={resolveIconButtonAriaLabel(
                        dictionary,
                        ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.CLEAR_SEARCH,
                    )}
                />
            )}
            <Button
                className="inline-flex shrink-0"
                variant={ButtonVariants.QUIET}
                type={ButtonTypes.SUBMIT}
                icon={<SearchIcon />}
                data-testid={`${testIdPrefix}search-button`}
                aria-label={resolveIconButtonAriaLabel(dictionary, ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.SUBMIT_SEARCH)}
            />
        </form>
    )
}
